'use client';

import { FC, RefObject, useEffect, useId, useRef } from 'react';
import classNames from 'classnames';

import { Button, Close, DiscountsSection, Heading, Portal } from '@/components';
import { useIsMobile } from '@/hooks';
import { useRefinementsHelpers } from '@/opensearch';

import { FiltersList } from '../FiltersTreez.components';
import { FiltersModalFooter } from './FiltersModal.components';

import styles from './FiltersModal.module.scss';

export const FILTERS_DRAWER_ID = 'filters-treez-drawer';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const getVisibleFocusable = (root: HTMLElement) =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    el =>
      !el.closest('[hidden], [aria-hidden="true"]') &&
      el.getClientRects().length > 0,
  );

interface IFiltersModal {
  isOpen: boolean;
  showDealsFilter?: boolean;
  onClose: () => void;
  /** Filter trigger — focus returns here when the drawer closes. */
  triggerRef?: RefObject<HTMLButtonElement | null>;
}

const FiltersModal: FC<IFiltersModal> = ({
  showDealsFilter,
  onClose,
  isOpen,
  triggerRef,
}) => {
  const { isMobile } = useIsMobile();
  const { clearAllFilters } = useRefinementsHelpers();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const wasOpenRef = useRef(false);

  // Move focus into the drawer on open; restore to FILTER on close.
  // modal={true} on Portal traps Tab; this covers initial focus + restore
  // when the trigger is outside Vaul's Dialog.Trigger pattern.
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      // Wait a tick so Vaul has mounted/shown content before focusing.
      const timer = window.setTimeout(() => {
        const panel = panelRef.current;
        if (!panel) return;
        const focusable = getVisibleFocusable(panel);
        const closeBtn = focusable.find(
          el => el.getAttribute('aria-label')?.toLowerCase() === 'close',
        );
        (closeBtn ?? focusable[0])?.focus();
      }, 50);
      return () => window.clearTimeout(timer);
    }

    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      triggerRef?.current?.focus();
    }
  }, [isOpen, triggerRef]);

  return (
    <Portal
      accessibilityTitle="Filters"
      activeSnapPoint={1}
      classContent={styles.filters_modal}
      direction={isMobile ? 'bottom' : 'left'}
      // Keep desktop overlay dismiss via Vaul; modal enables focus trap + inert.
      ignoreOutsideClick={false}
      isOpen={isOpen}
      modal
      nested={!isMobile}
      snapPoints={[1]}
      toggle={onClose}
    >
      <div
        aria-labelledby={titleId}
        className={styles.filters_panel}
        id={FILTERS_DRAWER_ID}
        ref={panelRef}
      >
        <div className={styles.filters_header}>
          <Heading
            className={styles.filters_header__title}
            id={titleId}
            level={2}
          >
            Filters
          </Heading>
          <div className={styles.filters_header__actions}>
            <Button
              className={styles.filters_header__clear}
              onClick={clearAllFilters}
            >
              CLEAR ALL
            </Button>
            <Close onClick={() => onClose()} />
          </div>
        </div>
        <div className={styles.filters_body}>
          <DiscountsSection />
          <FiltersList
            className={classNames(styles.filters_list__wrapper, {
              [styles.hidden]: !isOpen,
            })}
            showDealsFilter={showDealsFilter}
          />
        </div>
        <FiltersModalFooter onClose={onClose} />
      </div>
    </Portal>
  );
};

export { FiltersModal };
